import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import { categories, articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug],
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug],
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: activeCategory?.name || '分类',
    description: activeCategory?.description || '',
    url: `https://airluxx.github.io/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://airluxx.github.io/article/${article.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: activeCategory?.name || '分类' },
              ]}
            />

            <div className="mt-6 md:mt-8">
              <h1 className="font-heading text-xl md:text-3xl font-semibold text-foreground-900">
                {activeCategory?.name}
              </h1>
              <p className="mt-2 text-sm text-foreground-600">{activeCategory?.description}</p>
            </div>

            <div className="mt-6">
              <div className="flex items-center gap-1.5 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`px-4 py-2 text-sm rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-accent-100 text-accent-800">
                        {article.categoryName}
                      </span>
                      <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                    </div>
                    <h3 className="mt-2 text-sm md:text-base font-medium text-foreground-900 leading-snug line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-1.5 text-xs md:text-sm text-foreground-600 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime} 分钟
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}